/**
 * The socket's frames, as types and as two functions.
 *
 * This file is the wire and nothing else: no state, no store, no React. Its
 * source is `relay/docs/protocol.md` §2, and the constants below are the same
 * numbers `relay/tests/vectors/relay_protocol.json` carries — the vector file
 * is read by the Qt client's `protocol_smoke` and by relay-server's
 * `protocol.test.ts`, and `frames.test.ts` beside this file reads it too, so a
 * constant changed on one side breaks a test rather than a conversation.
 *
 * **A payload is opaque here.** Every `payload` field is base64 and stays
 * base64 through this file; `payload.ts` is the only thing that opens one. The
 * server never decodes them either, which is why threads, quotes, attachments,
 * `callend` and the meeting card all landed with no server change at all.
 *
 * **An unknown `t` is data, not an error.** The server's frame set is additive
 * and `PROTOCOL_VERSION` does not move for an addition — the `hello` check is
 * exact equality, so a bump would be a fatal close for every deployed client at
 * once. A frame this build has never heard of arrives as `UnknownFrame` and is
 * ignored by the store, which is the posture every shipped client already
 * takes.
 */

/** `hello`'s `p`. Exact equality on the server: do not move it for a new frame. */
export const PROTOCOL_VERSION = 1;

/** The caps, from the shared vector file. */
export const LIMITS = {
  maxFrameBytes: 196608,
  maxEnvelopeBytes: 131072,
  maxWatch: 512,
  maxPull: 256,
  defaultPull: 64,
  heartbeatSeconds: 45,
  liveFanoutMax: 32,
  /** Decoded, and **fatal**: the server closes the socket rather than dropping
   *  an ephemeral over it. Every ephemeral kind carries `n`, a display name
   *  nothing clamps, so the sender checks the length itself. */
  maxEphemeralBytes: 2048
} as const;

export const CLOSE = {
  goingAway: 1001,
  unauthorized: 4001,
  protocol: 4002,
  tooLarge: 4003,
  replaced: 4004,
  revoked: 4005
} as const;

/**
 * What to do about a close code.
 *
 * `1001` and `4001` produce identical silence if a client cannot tell them
 * apart, and one of them must reconnect while the other must not.
 *
 * `report` is "say so and stay down". Reconnecting after `4002` repeats
 * whatever the server refused, and after `4003` repeats a frame over the cap;
 * a client that reconnects into either has written a hot loop with a server on
 * the other end of it.
 *
 * `4006` is not in the table and is treated as fatal on purpose: an unknown
 * code in the 4000s is the server refusing this client for a reason it has not
 * been taught, and retrying a refusal is the one wrong answer. Anything else —
 * `1006` from a dropped network, most of all — reconnects.
 */
export type CloseAction = 'reconnect' | 'stop' | 'report';

export function closeAction(code: number): CloseAction {
  if (code === CLOSE.unauthorized || code === CLOSE.replaced || code === CLOSE.revoked) {
    return 'stop';
  }
  if (code === CLOSE.protocol || code === CLOSE.tooLarge) return 'report';
  if (code === 4006) return 'stop';
  return 'reconnect';
}

/**
 * Full jitter, exactly as `relay/src/core/backoff.h` draws it.
 *
 *     ceiling(n) = min(30000, 500 · 2^n)
 *     delay(n)   = uniform[0, ceiling(n)]
 *
 * Uniform across the *whole* interval, not a wobble around it. Every deploy of
 * the Worker drops every socket at the same instant — several times a week —
 * and a ±10% nudge on a shared 500 ms leaves the herd a herd.
 */
export function backoffCeilingMs(attempt: number): number {
  const ceiling = 500 * 2 ** Math.min(attempt, 16);
  return Math.min(30000, ceiling);
}

export function backoffDelayMs(attempt: number, random: () => number = Math.random): number {
  return Math.floor(random() * backoffCeilingMs(attempt));
}

// ── entries and conversations ───────────────────────────────────────────────

/** `application` is the only kind this client posts. The other three are the
 *  MLS era's, accepted and unused — a log that already held them kept its
 *  numbering when the model changed. */
export type EnvelopeKind = 'application' | 'proposal' | 'commit' | 'welcome';

/** One row of the log. `payload` is base64 and stays that way here. */
export interface LogEntry {
  seq: number;
  id: string;
  /** Session-stamped by the server. Authorship, and not forgeable. */
  sender: string;
  device: string;
  kind: EnvelopeKind;
  epoch: number;
  /** Server time, milliseconds. */
  at: number;
  payload: string;
  /** Members an organisation boundary kept this entry from — served to the
   *  sender and nobody else. The durable twin of `AckFrame.unreachable`, and
   *  the only source of the mark after a reload, since an ack reaches one
   *  device once. */
  unreachable?: string[];
}

export interface WireCallSeat {
  userId: string;
  deviceId: string;
  joinedAt: number;
}

/** The cold call state, riding in `sync`. Absent when no call is running. */
export interface SyncCall {
  since: number;
  callId: string;
  roster: WireCallSeat[];
}

export interface SyncConversation {
  c: string;
  head: number;
  epoch: number;
  availableFrom: number;
  /** The cursor as the server understood it, after clamping. */
  cursor: number;
  /** Every member's read position. Absent on a conversation with none. */
  reads?: Record<string, number>;
  call?: SyncCall;
}

// ── server → client ─────────────────────────────────────────────────────────

export interface SyncFrame {
  t: 'sync';
  p: number;
  heartbeat: number;
  conversations: SyncConversation[];
}
export interface DeliverFrame {
  t: 'deliver';
  c: string;
  entries: LogEntry[];
}
export interface NewsFrame {
  t: 'news';
  c: string;
  head: number;
}
export interface BatchFrame {
  t: 'batch';
  c: string;
  /** Where the answer **actually** starts, which may be higher than what was
   *  asked for: the server clamps a pull from below `joined_seq` silently. */
  from: number;
  entries: LogEntry[];
  head: number;
  more: boolean;
}
export interface GapFrame {
  t: 'gap';
  c: string;
  from: number;
  availableFrom: number;
}
export interface AckFrame {
  t: 'ack';
  c: string;
  id: string;
  seq: number;
  epoch: number;
  duplicate?: boolean;
  /** Members an organisation boundary kept this entry from. Absent when there
   *  are none, which is almost every ack. Never a block — relay-server
   *  discloses one of the two reasons it withholds and not the other; the
   *  contract is `relay-server/docs/protocol.md` § Telling the sender. */
  unreachable?: string[];
}
export interface NackFrame {
  t: 'nack';
  c: string;
  id: string;
  code: string;
  message: string;
  epoch?: number;
}
export interface AlertFrame {
  t: 'alert';
  code: string;
  message: string;
  c?: string;
  /** Additive, for `code: 'meeting'`. */
  meetingId?: string;
}
export interface EphFrame {
  t: 'eph';
  c: string;
  /** Session-stamped, exactly like an entry's `sender`. */
  from: string;
  device: string;
  payload: string;
}
export interface ReadPosFrame {
  t: 'readpos';
  c: string;
  user: string;
  seq: number;
}
export interface CallStateFrame {
  t: 'callstate';
  c: string;
  /** 0 when the roster is empty. */
  since: number;
  /** `''` when the roster is empty, or when nobody named it. */
  callId: string;
  roster: WireCallSeat[];
}
/** A frame from a newer server. Carried rather than thrown. */
export interface UnknownFrame {
  t: string;
  [key: string]: unknown;
}

export type ServerFrame =
  | SyncFrame
  | DeliverFrame
  | NewsFrame
  | BatchFrame
  | GapFrame
  | AckFrame
  | NackFrame
  | AlertFrame
  | EphFrame
  | ReadPosFrame
  | CallStateFrame
  | UnknownFrame;

// ── client → server ─────────────────────────────────────────────────────────

export interface WatchEntry {
  c: string;
  /** The highest seq already held. */
  seq: number;
}

export interface HelloFrame {
  t: 'hello';
  p: number;
  device: string;
  /** Replaces the watched set: anything watched before and not named here is
   *  unsubscribed. */
  watch: WatchEntry[];
}
export interface WatchFrame {
  t: 'watch';
  add?: WatchEntry[];
  remove?: string[];
}
export interface PullFrame {
  t: 'pull';
  c: string;
  from: number;
  limit?: number;
}
export interface PostFrame {
  t: 'post';
  c: string;
  /** Client-minted, and the server's dedup key. A retry reuses it. */
  id: string;
  kind: EnvelopeKind;
  epoch: number;
  payload: string;
}
export interface ReadFrame {
  t: 'read';
  c: string;
  seq: number;
}
export interface EphPostFrame {
  t: 'eph';
  c: string;
  payload: string;
}

export type ClientFrame =
  | HelloFrame
  | WatchFrame
  | PullFrame
  | PostFrame
  | ReadFrame
  | EphPostFrame;

/** The heartbeat. Answered by the runtime's auto-response pair **without
 *  waking the Durable Object**, which is the only reason one every 45 seconds
 *  is affordable. */
export const PING = '{"t":"ping"}';

export function encodeFrame(frame: ClientFrame): string {
  return JSON.stringify(frame);
}

/**
 * A text frame, or null for anything the frame reader must not see.
 *
 * Null covers three things and they are all ordinary: `{"t":"pong"}`, which is
 * not a protocol frame at all and is dropped here so nothing downstream has to
 * know it exists; text that is not JSON; and JSON that is not a frame. None of
 * them is worth an exception — a client that threw on one would turn a stray
 * frame into a dead socket.
 */
export function parseFrame(data: unknown): ServerFrame | null {
  if (typeof data !== 'string') return null;
  let value: unknown;
  try {
    value = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const frame = value as { t?: unknown };
  if (typeof frame.t !== 'string' || frame.t.length === 0) return null;
  if (frame.t === 'pong' || frame.t === 'ping') return null;
  return value as ServerFrame;
}

// ── base64, both ways ───────────────────────────────────────────────────────

/**
 * UTF-8 through base64, because `btoa` alone is not.
 *
 * `btoa` takes a latin1 string and throws on anything above U+00FF, so a
 * message with an emoji — or a name with an accent — would have failed to send
 * at all. The bytes go through `TextEncoder` first, which is what makes this
 * the same encoding Qt's `QJsonDocument::toJson().toBase64()` produces.
 */
export function encodeBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** The inverse. Returns null rather than throwing: a payload that is not
 *  base64 is an MLS-era row, which is skipped, not reported. */
export function decodeBase64(b64: string): string | null {
  try {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/** The decoded byte length of a string, for the ephemeral cap — which is
 *  counted in bytes, not characters, and one emoji is four of them. */
export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}
